import { useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { LifeBuoy, Loader2, Mail, Lightbulb } from "lucide-react";

export default function Support() {
  const [form, setForm] = useState({ subject: "", category: "general", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/support", form);
      toast.success(data.message);
      setForm({ subject: "", category: "general", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="support-page" className="max-w-2xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Help &amp; Support</h1>
      <p className="text-slate-500 mt-1">Have a question or found an issue? Your message goes directly to the DentiCision team at <a href="mailto:denticision@gmail.com" data-testid="support-email-link" className="text-sky-700 font-medium">denticision@gmail.com</a>.</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {[
          { icon: LifeBuoy, t: "Getting started", d: "Run a new evaluation from the sidebar." },
          { icon: Mail, t: "Response time", d: "We reply to your email within 1–3 business days." },
          { icon: Lightbulb, t: "Missing a condition or drug?", d: "Use “Suggest an Addition” in the sidebar." },
        ].map((x) => (
          <div key={x.t} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <x.icon className="w-5 h-5 text-sky-600" />
            <p className="font-medium text-slate-900 mt-2">{x.t}</p>
            <p className="text-sm text-slate-500 mt-0.5">{x.d}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-5 space-y-4">
        <div>
          <Label htmlFor="subject">Subject</Label>
          <Input id="subject" data-testid="support-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required className="mt-1.5" placeholder="Brief summary" />
        </div>
        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="support-category" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General question</SelectItem>
              <SelectItem value="clinical">Clinical content</SelectItem>
              <SelectItem value="billing">Billing / subscription</SelectItem>
              <SelectItem value="bug">Bug report</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="message">Message</Label>
          <Textarea id="message" data-testid="support-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5} className="mt-1.5" placeholder="Describe your question or issue..." />
        </div>
        <Button type="submit" disabled={loading} data-testid="support-submit" className="bg-slate-900 hover:bg-slate-800">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
        </Button>
      </form>
    </div>
  );
}
